import { isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppSelector } from '../../../hooks/react-redux';
import { capitaliseStartLetter } from '../../../utils/regex-utils';
import { Calculator } from '../../shared/calculator/Calculator';
import { newCalculatorField } from '../../shared/calculator/constants';
import { Dropdown, DropdownOption } from '../../shared/dropdown/Dropdown';
import { Calculator as CalculatorIcon } from '../../shared/icons';
import { ConfirmationModal } from '../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import { CalculatorField, CalculatorPromptDB, CalculatorPromptUI, getAllCalculatorFields, getCalculatorFieldTypeOrNull } from '../calculator/store';
import styles from './SharedAdmin.module.scss';

interface CalculatorPromptModalProps {
    calculatorPrompt: CalculatorPromptUI;
    width: number;
    getRelevantPrompts: (fieldType: string) => CalculatorPromptDB[];
    hasUpdated: boolean;
    closeModal: () => void;
    updatePrompt: (calculatorPrompt: CalculatorPromptUI) => void;
    saveCalculatorPrompt: () => void;
}

export const CalculatorPromptModal: React.FC<CalculatorPromptModalProps> = ({
    calculatorPrompt,
    width,
    getRelevantPrompts,
    hasUpdated,
    closeModal,
    updatePrompt,
    saveCalculatorPrompt
}) => {
    const { calculation, calculationText, calculatorFieldType, promptId, isSystem } = calculatorPrompt;
    const allCalculatorFieldTypes = useAppSelector(getAllCalculatorFields);
    const fieldType = useAppSelector(getCalculatorFieldTypeOrNull(calculatorFieldType));

    const fieldTypeOptions = useMemo(() => allCalculatorFieldTypes.map(({ name }) => ({ value: name, label: capitaliseStartLetter(name) })), [allCalculatorFieldTypes]);
    const fieldTypeDropdownValue = useMemo(() => calculatorFieldType ? fieldTypeOptions.find(({ value }) => value === calculatorFieldType)! : null, [calculatorFieldType, fieldTypeOptions]);

    const updateFieldType = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        if (!isNull(dropdownValue) && (dropdownValue as DropdownOption).value !== calculatorFieldType) {
            const value = (dropdownValue as DropdownOption).value;
            const priorityOrder = getRelevantPrompts(value).length + 1;
            const updatedPrompt: CalculatorPromptUI = {
                promptId,
                calculationText,
                calculatorFieldType: value,
                calculation: [newCalculatorField(null)],
                priorityOrder,
                isSystem
            };
            updatePrompt(updatedPrompt);
        }
    }, [updatePrompt, calculatorFieldType, promptId, calculationText, isSystem, getRelevantPrompts]);

    const updateCalculationText = useCallback((calculationText: string) => updatePrompt({ ...calculatorPrompt, calculationText }), [updatePrompt, calculatorPrompt]);
    const updateCalculation = useCallback((calculation: CalculatorField[]) => updatePrompt({ ...calculatorPrompt, calculation }), [updatePrompt, calculatorPrompt]);

    const showGracePeriod = useMemo(() => !!fieldType && !!fieldType.includeGracePeriod, [fieldType]);
    const showOutputField = useMemo(() => !!fieldType && !!fieldType.includeOutput, [fieldType]);

    const saveDisabled = useMemo(() => !hasUpdated || isNull(calculatorFieldType) || calculationText.length === 0, [hasUpdated, calculatorFieldType, calculationText]);

    return (
        <ConfirmationModal
            isOpen
            closeModal={closeModal}
            confirmDisabled={saveDisabled}
            confirmLabel='Save'
            confirm={saveCalculatorPrompt}
        >
            <div style={{ width: `${width}px` }}>
                <ModalHeader icon={CalculatorIcon} label='Calculator Prompt' />
                <div className={styles.promptModalWrapper}>
                    <div className={styles.calculatorFieldTypeWrapper}>
                        <div className={styles.promptTitle}>Calculator Field Type</div>
                        <Dropdown
                            options={fieldTypeOptions}
                            value={fieldTypeDropdownValue}
                            onChange={updateFieldType}
                            isClearable={false}
                            disabled={!isUndefined(promptId)}
                        />
                    </div>
                    <div className={styles.calculationTextWrapper}>
                        <div className={styles.promptTitle}>Calculation Text</div>
                        <textarea
                            className={styles.calculationText}
                            value={calculationText}
                            onChange={e => updateCalculationText(e.target.value)}
                        />
                    </div>
                    {!isNull(calculatorFieldType) &&
                        <div className={styles.calculationWrapper}>
                            <div className={styles.promptTitle}>Calculation</div>
                            <Calculator
                                value={calculation}
                                updateValue={updateCalculation}
                                isEditing
                                fieldType={calculatorFieldType}
                                width={width}
                                testId='calculator-prompt'
                                showOutputField={showOutputField}
                                showGracePeriod={showGracePeriod}
                            />
                        </div>
                    }
                </div>
            </div>
        </ConfirmationModal>
    );
};
